import { useEffect, useRef, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Mic, Square, Pause, Play, Loader2, Sparkles, Trash2, FileText, CalendarClock, MessageSquare } from "lucide-react";

const MAX_SECONDS = 5 * 60; // 5-minute cap per meeting note — far under Whisper's 25MB ceiling

function fmtMMSS(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const r = (s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

/**
 * Voice journal dialog scoped to a single deal.
 *  Step 1 (record) → Step 2 (transcribe + summarise) → Step 3 (review & save)
 *
 * On save we POST:
 *   - 1× Activity (type=note, deal/client linked) containing the AI summary.
 *   - Optionally 1× Follow-up if the rep keeps the suggestion.
 */
export default function DealVoiceJournalDialog({ open, onOpenChange, deal, onSaved }) {
  const [phase, setPhase] = useState("idle");   // idle | recording | processing | review
  const [seconds, setSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [summary, setSummary] = useState("");
  const [followupOn, setFollowupOn] = useState(false);
  const [followupDate, setFollowupDate] = useState("");
  const [followupAction, setFollowupAction] = useState("");
  const [saving, setSaving] = useState(false);
  const [paused, setPaused] = useState(false);

  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Full reset whenever the dialog re-opens — never carry stale state from
  // an earlier deal into a new dialog session.
  useEffect(() => {
    if (open) {
      setPhase("idle");
      setSeconds(0);
      setAudioBlob(null);
      setTranscript("");
      setSummary("");
      setFollowupOn(false);
      setFollowupDate("");
      setFollowupAction("");
      setPaused(false);
    } else {
      // Stop any active recording when the dialog closes.
      try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch (e) {}
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [open]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const opts = { audioBitsPerSecond: 24000 };
      if (mime) opts.mimeType = mime;
      const rec = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setAudioBlob(blob);
        try { stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
      };
      rec.start();
      recRef.current = rec;
      setPhase("recording");
      setPaused(false);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) { try { rec.stop(); } catch (e) {} clearInterval(timerRef.current); }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error("Microphone permission required");
    }
  };

  const togglePause = () => {
    const rec = recRef.current;
    if (!rec) return;
    if (rec.state === "recording") { rec.pause(); setPaused(true); clearInterval(timerRef.current); }
    else if (rec.state === "paused") {
      rec.resume(); setPaused(false);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
  };

  const stopAndProcess = async () => {
    try {
      const rec = recRef.current;
      if (rec && rec.state !== "inactive") rec.stop();
      clearInterval(timerRef.current);
      setPhase("processing");
      // Give onstop a beat to populate audioBlob.
      const blob = await new Promise((resolve) => {
        const check = () => {
          if (chunksRef.current.length === 0) { setTimeout(check, 100); return; }
          resolve(new Blob(chunksRef.current, { type: "audio/webm" }));
        };
        check();
      });
      const fd = new FormData();
      fd.append("file", blob, "deal-note.webm");
      const t = await api.post("/ai/voice-journal/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      const transcriptText = (t.data?.transcript || "").trim();
      if (!transcriptText) { toast.error("Couldn't hear you — try again"); setPhase("idle"); return; }
      setTranscript(transcriptText);
      // Run Gemini summary in parallel of UI showing the transcript.
      let parsed = {};
      try {
        const s = await api.post("/ai/meeting-summary", { raw_text: transcriptText });
        parsed = s.data?.extracted || {};
      } catch (err) {
        // Soft-fail — user can still log a manually-typed summary.
        console.warn("summary failed", err);
      }
      const summaryText = parsed.summary || parsed.key_points?.join("\n• ") || transcriptText.slice(0, 240);
      setSummary(summaryText.startsWith("•") ? summaryText : summaryText);
      // Suggested follow-up
      const fu = (parsed.follow_ups && parsed.follow_ups[0]) || null;
      if (fu) {
        setFollowupOn(true);
        setFollowupAction(fu.title || parsed.next_action || "");
        setFollowupDate(fu.due_date || parsed.follow_up_date || "");
      } else if (parsed.next_action) {
        setFollowupOn(true);
        setFollowupAction(parsed.next_action);
        setFollowupDate(parsed.follow_up_date || "");
      }
      setPhase("review");
    } catch (err) {
      toast.error(fmtError(err));
      setPhase("idle");
    }
  };

  const save = async () => {
    if (!summary.trim()) { toast.error("Summary required"); return; }
    setSaving(true);
    try {
      await api.post("/activities", {
        type: "note",
        deal_id: deal?.id,
        client_id: deal?.client_id,
        summary: summary.split("\n")[0].slice(0, 140),
        details: `${summary}\n\n—— Transcript ——\n${transcript}`,
      });
      if (followupOn && followupAction.trim()) {
        try {
          await api.post("/followups", {
            deal_id: deal?.id,
            client_id: deal?.client_id,
            due_date: followupDate
              ? new Date(followupDate.length === 10 ? followupDate + "T12:00:00" : followupDate).toISOString()
              : new Date(Date.now() + 86400000).toISOString(),
            next_action: followupAction.trim(),
          });
        } catch (err) {
          // Fall back to attaching to the deal directly if the followups
          // endpoint doesn't accept this shape — non-fatal.
          console.warn("follow-up create failed", err);
        }
      }
      toast.success("Activity logged");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="deal-voice-journal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
            <Mic className="h-5 w-5 text-primary" /> Voice journal
          </DialogTitle>
        </DialogHeader>

        {phase === "idle" && (
          <div className="text-center py-6 space-y-3">
            <div className="mx-auto h-20 w-20 rounded-full bg-primary-50 grid place-items-center">
              <Mic className="h-8 w-8 text-primary" />
            </div>
            <p className="font-semibold">Record a quick voice note</p>
            <p className="text-xs text-muted-foreground max-w-xs mx-auto">We'll transcribe, summarise, and suggest a follow-up. You'll review everything before it's saved.</p>
            <Button onClick={start} className="rounded-2xl mt-2" data-testid="vj-start">
              <Mic className="h-4 w-4 mr-1" /> Start recording
            </Button>
          </div>
        )}

        {phase === "recording" && (
          <div className="text-center py-6 space-y-4">
            <div className="mx-auto h-24 w-24 rounded-full bg-red-50 grid place-items-center animate-pulse">
              <Mic className="h-10 w-10 text-red-600" />
            </div>
            <p className="font-mono text-2xl font-bold tabular-nums" data-testid="vj-timer">{fmtMMSS(seconds)}</p>
            <p className="text-xs text-muted-foreground">{paused ? "Paused" : "Listening — talk through who you met, what was discussed, and what's next."}</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={togglePause} className="rounded-2xl" data-testid="vj-pause">
                {paused ? <><Play className="h-4 w-4 mr-1" /> Resume</> : <><Pause className="h-4 w-4 mr-1" /> Pause</>}
              </Button>
              <Button onClick={stopAndProcess} className="rounded-2xl" data-testid="vj-stop">
                <Square className="h-4 w-4 mr-1" /> Stop &amp; process
              </Button>
            </div>
          </div>
        )}

        {phase === "processing" && (
          <div className="text-center py-10 space-y-3">
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />
            <p className="font-semibold">Transcribing &amp; summarising…</p>
            <p className="text-xs text-muted-foreground">Usually takes 5–20 seconds.</p>
          </div>
        )}

        {phase === "review" && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-primary" /> AI summary</Label>
              <Textarea className="rounded-2xl mt-1.5" rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="vj-summary" />
              <p className="text-[10px] text-muted-foreground mt-1">Becomes the activity log entry.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Raw transcript</Label>
              <Textarea className="rounded-2xl mt-1.5 font-mono text-xs" rows={3} value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="vj-transcript" />
            </div>
            <div className="rounded-2xl border border-border/60 p-3 bg-muted/20">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={followupOn}
                  onChange={(e) => setFollowupOn(e.target.checked)}
                  className="h-4 w-4 rounded text-primary"
                  data-testid="vj-followup-toggle"
                />
                <span className="font-semibold text-sm flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-primary" /> Create follow-up</span>
                {followupOn && <Badge variant="outline" className="ml-auto text-[10px] bg-primary-50 text-primary-700 border-primary-200">AI suggested</Badge>}
              </label>
              {followupOn && (
                <div className="grid grid-cols-2 gap-2 mt-2.5">
                  <div>
                    <Label className="text-[10px] font-semibold">Due date</Label>
                    <Input type="date" className="rounded-2xl h-10 mt-1 text-sm" value={(followupDate || "").slice(0, 10)} onChange={(e) => setFollowupDate(e.target.value)} data-testid="vj-followup-date" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold">Action</Label>
                    <Input className="rounded-2xl h-10 mt-1 text-sm" value={followupAction} onChange={(e) => setFollowupAction(e.target.value)} placeholder="Send proposal" data-testid="vj-followup-action" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {(phase === "idle" || phase === "review") && (
          <DialogFooter>
            <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>
              {phase === "review" ? <><Trash2 className="h-4 w-4 mr-1" /> Discard</> : "Cancel"}
            </Button>
            {phase === "review" && (
              <Button onClick={save} disabled={saving} className="rounded-2xl" data-testid="vj-save">
                {saving ? "Saving…" : <><MessageSquare className="h-4 w-4 mr-1" /> Save activity</>}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
